/**
 * Messaging API client — thin typed wrapper over the Pheme service
 * (francisquiniai/pheme). Base URL comes from `runtime-config.api.pheme`;
 * X-Tenant-Id + Bearer headers, JSON envelope `{data, meta}` or `{error}`.
 * Only the two endpoints `useMessages` needs (list + dismiss).
 */
import { resolveApiBase, tenantAdminRequest, type AdminRequestOptions } from '@/lib/http/adminRequest'
import { isValidPhemeMessageId } from '@francisquiniai/cuidari-core/messaging'
import {
  type DismissResponse,
  type MessagesResponse,
  type PlacementSlot,
  MessagingApiError,
} from './types'

async function request<T>(
  path: string,
  tenantId: string,
  options: AdminRequestOptions = {},
): Promise<T> {
  return tenantAdminRequest<T>({
    root: await resolveApiBase('pheme'),
    path,
    tenantId,
    label: 'Messaging',
    makeError: (message, status, body) => new MessagingApiError(message, status, body),
    options,
  })
}

interface ApiEnvelope<T> {
  data: T
  meta: { request_id: string; version: string }
}

export async function listMessages(
  tenantId: string,
  placement: PlacementSlot,
  context?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<MessagesResponse> {
  const qs = new URLSearchParams({ placement })
  if (context && Object.keys(context).length > 0) {
    qs.set('context', JSON.stringify(context))
  }
  const env = await request<ApiEnvelope<MessagesResponse>>(
    `/v1/messages?${qs.toString()}`,
    tenantId,
    { signal },
  )
  return env.data
}

export async function dismissMessage(
  tenantId: string,
  messageId: string,
): Promise<DismissResponse> {
  if (!isValidPhemeMessageId(messageId)) {
    return {
      message_id: messageId,
      dismissed_at: new Date().toISOString(),
      ttl_epoch: Math.floor(Date.now() / 1000),
    }
  }
  const env = await request<ApiEnvelope<DismissResponse>>(
    `/v1/messages/${encodeURIComponent(messageId)}/dismiss`,
    tenantId,
    { method: 'POST' },
  )
  return env.data
}
